import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AutoComplete, Form, Input, Table, Tag } from 'tdesign-react';
import { useMemoizedFn } from 'ahooks';
import { trim } from 'lodash';
import styled from 'styled-components';
import useTable from '@/hooks/useTable';
import { renderByteRate, renderDateTime, renderNumRate, sleep } from '@/utils/common';
import { getAllTopics, getProduceClients } from '@/services/utilService';
import PopupLink from '@/components/Cluster/PopupLink';
import SystemSwitchAppend from '@/components/Cluster/SystemSwitchAppend';

const { FormItem } = Form;

export default function Producer() {
  const [searchParams] = useSearchParams();
  const clusterName = searchParams.get('name');
  const [topicOptions, setTopicOptions] = useState([]);
  const [systemTopic, setSystemTopic] = useState(false);
  const navigate = useNavigate();

  const loadData = useMemoizedFn(async (params = {}) => {
    const { topic, ...others } = params;

    const { data } = await getProduceClients({
      clusterName,
      ...others,
      topic: trim(topic),
      containSystemTopic: systemTopic,
    });

    setData(data.items, data.count);
  });

  const loadOptions = useMemoizedFn(async () => {
    const resp = await getAllTopics({
      clusterName,
    });
    setTopicOptions(resp.data);
  });

  const columns = [
    {
      title: '生产者名称',
      colKey: 'producerName',
      fixed: 'left',
      ellipsis: true,
      sorter: true,
      width: 180,
    },
    {
      title: '生产者地址',
      colKey: 'address',
      ellipsis: true,
      sorter: true,
      width: 180,
    },
    {
      title: '客户端版本',
      colKey: 'clientVersion',
      ellipsis: true,
      sorter: true,
      width: 130,
    },
    {
      title: '生产吞吐',
      colKey: 'bytesIn',
      sorter: true,
      width: 120,
      cell: renderByteRate,
    },
    {
      title: '生产速率',
      sorter: true,
      colKey: 'rateIn',
      width: 120,
      cell: renderNumRate,
    },
    {
      title: 'Topic',
      sorter: true,
      colKey: 'topic',
      width: 380,
      cell: ({ row }) => (
        <PopupLink
          tip='跳转Topic'
          onClick={() => {
            navigate(`/cluster/topic/list?name=${clusterName}&topic=${row.topic}`);
          }}
        >
          {row.systemTopic && (
            <Tag style={{ marginRight: 6 }} theme='danger'>
              system
            </Tag>
          )}
          {row.topic}
        </PopupLink>
      ),
    },
    {
      title: 'Partition',
      sorter: true,
      colKey: 'partition',
      width: 100,
    },
    {
      title: '连接时间',
      sorter: true,
      colKey: 'connectedSince',
      cell: renderDateTime,
      width: 180,
    },
    {
      title: '归属应用',
      sorter: true,
      colKey: 'app',
      ellipsis: true,
      width: 120,
    },
    {
      title: '采集时间',
      colKey: 'updateTime',
      cell: renderDateTime,
      width: 180,
    },
  ];

  const onSystemSwitch = async (isContainSystemTopic) => {
    setSystemTopic(isContainSystemTopic);
    await sleep(30);
    doSearch(null, true);
  };

  const { setData, header, config, doSearch, form } = useTable({
    columns,
    onSearch: loadData,
    search: (
      <>
        <FormItem name='producerName'>
          <Input placeholder='请输入生产者名' clearable />
        </FormItem>
        <FormItem name='topic'>
          <AutoComplete
            style={{ width: '100%' }}
            popupProps={{ overlayInnerStyle: { width: 'auto' } }}
            options={topicOptions}
            placeholder='请输入Topic'
            clearable
          />
        </FormItem>
        <FormItem name='app'>
          <Input placeholder='请输入应用' clearable />
        </FormItem>

        <FormItem name='address'>
          <Input placeholder='请输入地址' clearable />
        </FormItem>
      </>
    ),
    append: <SystemSwitchAppend value={systemTopic} onChange={onSystemSwitch} />,
    remote: true,
  });

  const doInit = useMemoizedFn(async () => {
    setSystemTopic(false);
    loadOptions();
    form.reset();
    await sleep(30);

    doSearch();
  });

  useEffect(() => {
    if (clusterName) {
      doInit();
    }
  }, [doInit, clusterName]);

  return (
    <Root>
      {header}
      <Table {...config} />
    </Root>
  );
}

const Root = styled.div`
  margin-top: 20px;
`;
